/**
 * Trunnion AI - Agent Detail View
 * Full agent configuration display.
 */

import { useNavigate } from 'react-router-dom';
import { Bot, Wrench, Clock, Hash, Tag, MessageSquare } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { CodeBlock } from '@/components/ui/CodeBlock';
import { FavoriteButton } from '@/components/ui/FavoriteButton';
import { ROUTES } from '@/config/routes';
import type { AgentIdentity } from '@/types';

interface AgentDetailViewProps {
  agent: AgentIdentity;
}

export function AgentDetailView({ agent }: AgentDetailViewProps) {
  const navigate = useNavigate();

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-trunnion-gold-500/10">
          <Bot className="h-7 w-7 text-trunnion-gold-500" />
        </div>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <h1 className="text-xl font-bold text-white">{agent.name}</h1>
            <FavoriteButton type="agent" id={agent.id} label={agent.name} />
            <ClassificationTag level={agent.maxClassification} size="md" />
            {!agent.isActive && <Badge variant="default">Inactive</Badge>}
            <Button
              variant="secondary"
              size="sm"
              icon={<MessageSquare className="h-4 w-4" />}
              onClick={() => navigate(ROUTES.AGENT_CHAT(agent.id))}
            >
              Open Chat
            </Button>
          </div>
          <p className="mt-1 text-sm text-trunnion-slate-400">{agent.description}</p>
          <div className="mt-2 flex items-center gap-4 text-xs text-trunnion-slate-400">
            <span className="flex items-center gap-1">
              <Hash className="h-3 w-3" />
              v{agent.version}
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              Updated {new Date(agent.updatedAt).toLocaleDateString()}
            </span>
            {agent.tags.length > 0 && (
              <span className="flex items-center gap-1">
                <Tag className="h-3 w-3" />
                {agent.tags.join(', ')}
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {/* Configuration */}
        <Card>
          <h3 className="text-sm font-semibold text-white mb-3">Configuration</h3>
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-trunnion-slate-400">Tier</span>
              <span className="text-white capitalize">{agent.tier}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-trunnion-slate-400">Mode</span>
              <span className="text-white capitalize">{agent.mode}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-trunnion-slate-400">LLM Provider</span>
              <span className="text-white">{agent.llmProviderId}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-trunnion-slate-400">Model</span>
              <span className="text-white">{agent.llmModelId}</span>
            </div>
          </div>
        </Card>

        {/* Constraints */}
        <Card>
          <h3 className="text-sm font-semibold text-white mb-3">Constraints</h3>
          {agent.constraints.length === 0 ? (
            <p className="text-sm text-trunnion-slate-400">No constraints defined</p>
          ) : (
            <div className="space-y-2">
              {agent.constraints.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between rounded-lg border border-trunnion-slate-700 bg-trunnion-slate-800/50 px-3 py-2"
                >
                  <div>
                    <p className="text-sm text-white">{c.description}</p>
                    <p className="text-xs text-trunnion-slate-400">
                      {c.type}: {c.value}
                    </p>
                  </div>
                  <Badge variant={c.enforcementMode === 'hard' ? 'error' : 'warning'}>
                    {c.enforcementMode}
                  </Badge>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Tool Whitelist */}
        <Card>
          <h3 className="text-sm font-semibold text-white mb-3">
            <Wrench className="h-4 w-4 inline mr-1.5" />
            Tool Whitelist ({agent.toolWhitelist.length})
          </h3>
          {agent.toolWhitelist.length === 0 ? (
            <p className="text-sm text-trunnion-slate-400">No tools assigned</p>
          ) : (
            <div className="space-y-1.5">
              {agent.toolWhitelist.map((tool) => (
                <div
                  key={tool.toolId}
                  className="flex items-center justify-between rounded border border-trunnion-slate-700 px-2.5 py-1.5 text-sm"
                >
                  <span className="text-white">{tool.toolName}</span>
                  <div className="flex items-center gap-2">
                    {tool.requiresApproval && (
                      <Badge variant="warning">Approval</Badge>
                    )}
                    <span className="text-xs text-trunnion-slate-400">
                      max {tool.maxCallsPerExecution}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* System Prompt */}
        <Card>
          <h3 className="text-sm font-semibold text-white mb-3">System Prompt</h3>
          <CodeBlock
            content={agent.systemPrompt || 'No system prompt defined'}
            language="text"
            maxHeight="200px"
          />
        </Card>
      </div>
    </div>
  );
}
